import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NgbDropdownModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { ClipboardModule } from 'ngx-clipboard';
import { NgxStripeModule } from 'ngx-stripe';
import { QRCodeComponent } from 'angularx-qrcode';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { environment } from '@env/environment';

import { SharedModule } from '@app/shared';
import { LcurrencyPipe } from '@app/shared/pipes';
import { ConfigurationRoutingModule } from './configuration-routing.module';
import { ConfigurationsIndexComponent } from './configurations-index/configurations-index.component';
import { ConfigurationsStandaloneIndexComponent } from './configurations-standalone-index/configurations-standalone-index.component';
import { ConfigurationsProfileComponent } from './configurations-profile/configurations-profile.component';
import { EditProfileModalComponent } from './configurations-profile/edit-profile-modal/edit-profile-modal.component';
import { EditSignatureModalComponent } from './configurations-profile/edit-signature-modal/edit-signature-modal.component';
import { SignaturePreviewComponent } from './configurations-profile/signature-preview/signature-preview.component';
import { ConfigurationsAppearanceComponent } from './configurations-appearance/configurations-appearance.component';
import { ConfigurationsNotificationComponent } from './configurations-notification/configurations-notification.component';
import { ConfigurationsSecurityComponent } from './configurations-security/configurations-security.component';
import { EditEmailModalComponent } from './configurations-security/edit-email-modal/edit-email-modal.component';
import { EditPasswordModalComponent } from './configurations-security/edit-password-modal/edit-password-modal.component';
import { AddMfaModalComponent } from './configurations-security/add-mfa-modal/add-mfa-modal.component';
import { ConfigurationsPlanComponent } from './configurations-plan/configurations-plan.component';
import { EditPaymentInfoModalComponent } from './configurations-plan/edit-payment-info-modal/edit-payment-info-modal.component';
import { CustomCreditPackModalComponent } from './configurations-plan/additional-validation/custom-credit-pack-modal/custom-credit-pack-modal.component';
import { PartnersPlanModalComponent } from './configurations-plan/partners-plan-modal/partners-plan-modal.component';
import { ConfigurationsApiComponent } from './configurations-api/configurations-api.component';
import { ConfigurationsOAuthClientsComponent } from './configurations-oauth-clients/configurations-oauth-clients.component';
import { OAuthClientModalComponent } from './configurations-oauth-clients/oauth-client-modal/oauth-client-modal.component';
import { OAuthClientCredentialsModalComponent } from './configurations-oauth-clients/oauth-client-credentials-modal/oauth-client-credentials-modal.component';
import { ConfigurationsWebhooksComponent } from './configurations-webhooks/configurations-webhooks.component';
import { ConfigurationsOrganizationComponent } from './configurations-organization/configurations-organization.component';
import { EditOrganizationModalComponent } from './configurations-organization/edit-organization-modal/edit-organization-modal.component';
import { DeleteSelfOrganizationMemberModalComponent } from './configurations-organization/delete-self-organization-member-modal/delete-self-organization-member-modal.component';
import { ConfigurationsOrganizationMembersComponent } from './configurations-organization-members/configurations-organization-members.component';
import { OrganizationPermissionsFormComponent } from './configurations-organization-members/organization-permissions-form/organization-permissions-form.component';
import { InviteOrganizationMembersModalComponent } from './configurations-organization-members/invite-organization-members-modal/invite-organization-members-modal.component';
import { EditOrganizationMemberModalComponent } from './configurations-organization-members/edit-organization-member-modal/edit-organization-member-modal.component';
import { ConfigurationsOrganizationGroupsComponent } from './configurations-organization-groups/configurations-organization-groups.component';
import { CreateOrganizationGroupModalComponent } from './configurations-organization-groups/create-organization-group-modal/create-organization-group-modal.component';
import { EditOrganizationGroupModalComponent as EditGroupModalComponent } from './configurations-organization-groups/edit-organization-group-modal/edit-organization-group-modal.component';
import { EditOrganizationGroupPermissionsModalComponent } from './configurations-organization-groups/edit-organization-group-permissions-modal/edit-organization-group-permissions-modal.component';
import { EditOrganizationGroupAppearanceModalComponent } from './configurations-organization-groups/edit-organization-group-appearance-modal/edit-organization-group-appearance-modal.component';
import { ConfigurationsReferralsComponent } from './configurations-referrals/configurations-referrals.component';
import { ConfigurationsOrganizationEmailsComponent } from './configurations-organization-emails/configurations-organization-emails.component';
import { SignatureEmailPreviewComponent } from './configurations-organization-emails/signature-email-preview/signature-email-preview.component';
import { ParseEmailVariablesPipe } from './configurations-organization-emails/signature-email-preview/parse-email-variables.pipe';
import { ConfigurationsWhitelabelComponent } from './configurations-whitelabel/configurations-whitelabel.component';
import { WhitelabelDomainNameComponent } from './configurations-whitelabel/whitelabel-domain-name/whitelabel-domain-name.component';
import { WhitelabelEmailAdressComponent } from './configurations-whitelabel/whitelabel-email-adress/whitelabel-email-adress.component';
import { WhitelabelSignedDocumentComponent } from './configurations-whitelabel/whitelabel-signed-document/whitelabel-signed-document.component';
import { WhitelabelSigningPageComponent } from './configurations-whitelabel/whitelabel-signing-page/whitelabel-signing-page.component';
import { SigningPageComponent } from './configurations-whitelabel/whitelabel-shared/signing-page/signing-page.component';
import { SignedDocumentComponent } from './configurations-whitelabel/whitelabel-shared/signed-document/signed-document.component';
import { BluredSignerCardComponent } from './configurations-whitelabel/whitelabel-shared/blured-signer-card/blured-signer-card.component';
import { BluredDocumentComponent } from './configurations-whitelabel/whitelabel-shared/blured-document/blured-document.component';
import { BluredSignerComponent } from './configurations-whitelabel/whitelabel-shared/blured-signer/blured-signer.component';
import { BluredEventComponent } from './configurations-whitelabel/whitelabel-shared/blured-event/blured-event.component';
import { EditLocationModalComponent } from '@app/pages/configurations/configurations-profile/edit-location-modal/edit-location-modal.component';
import { CreateEndpointModalComponent } from '@app/pages/configurations/configurations-webhooks/create-endpoint-modal/create-endpoint-modal.component';
import { EndpointSecretModalComponent } from '@app/pages/configurations/configurations-webhooks/endpoint-secret-modal/endpoint-secret-modal.component';
import { ApiUsageComponent } from '@app/pages/configurations/configurations-plan/api-usage/api-usage.component';
import { AdditionalValidationComponent } from '@app/pages/configurations/configurations-plan/additional-validation/additional-validation.component';
import { WhitelabelOpengraphModalComponent } from '@app/pages/configurations/configurations-whitelabel/whitelabel-opengraph-modal/whitelabel-opengraph-modal.component';
import { ApiUsageLimitsModalComponent } from '@app/pages/configurations/configurations-plan/api-usage-limits-modal/api-usage-limits-modal.component';
import { InvoicesModule } from '@app/pages/invoices/invoices.module';

@NgModule({
  declarations: [
    ConfigurationsProfileComponent,
    ConfigurationsAppearanceComponent,
    ConfigurationsNotificationComponent,
    ConfigurationsSecurityComponent,
    ConfigurationsPlanComponent,
    ConfigurationsApiComponent,
    ConfigurationsOAuthClientsComponent,
    OAuthClientModalComponent,
    OAuthClientCredentialsModalComponent,
    ConfigurationsWebhooksComponent,
    ConfigurationsOrganizationComponent,
    ConfigurationsIndexComponent,
    ConfigurationsStandaloneIndexComponent,
    EditProfileModalComponent,
    EditEmailModalComponent,
    EditPasswordModalComponent,
    AddMfaModalComponent,
    EditSignatureModalComponent,
    InviteOrganizationMembersModalComponent,
    EditOrganizationModalComponent,
    DeleteSelfOrganizationMemberModalComponent,
    EditPaymentInfoModalComponent,
    CustomCreditPackModalComponent,
    PartnersPlanModalComponent,
    SignaturePreviewComponent,
    SignatureEmailPreviewComponent,
    ConfigurationsOrganizationMembersComponent,
    ParseEmailVariablesPipe,
    OrganizationPermissionsFormComponent,
    EditOrganizationMemberModalComponent,
    ConfigurationsOrganizationGroupsComponent,
    CreateOrganizationGroupModalComponent,
    EditGroupModalComponent,
    EditOrganizationGroupPermissionsModalComponent,
    EditOrganizationGroupAppearanceModalComponent,
    ConfigurationsReferralsComponent,
    ConfigurationsOrganizationEmailsComponent,
    ConfigurationsWhitelabelComponent,
    WhitelabelDomainNameComponent,
    WhitelabelEmailAdressComponent,
    WhitelabelSignedDocumentComponent,
    WhitelabelSigningPageComponent,
    SigningPageComponent,
    SignedDocumentComponent,
    BluredSignerCardComponent,
    BluredDocumentComponent,
    BluredSignerComponent,
    BluredEventComponent,
    EditLocationModalComponent,
    CreateEndpointModalComponent,
    EndpointSecretModalComponent,
    ApiUsageComponent,
    AdditionalValidationComponent,
    WhitelabelOpengraphModalComponent,
    ApiUsageLimitsModalComponent
  ],
  imports: [
    SharedModule,
    NgbDropdownModule,
    NgbTooltipModule,
    CommonModule,
    RouterModule,
    ConfigurationRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    ClipboardModule,
    QRCodeComponent,
    NgxStripeModule.forRoot(environment.stripePublicKey),
    NgxChartsModule,
    NgxDaterangepickerMd,
    InvoicesModule
  ],
  providers: [ParseEmailVariablesPipe, LcurrencyPipe],
  exports: [SignatureEmailPreviewComponent]
})
export class ConfigurationModule {}
